import { queryOptions } from "@tanstack/react-query";
import { analysisClient } from "@/shared/lib/supabase";
import { getKeyOnlyQueryConfig } from "@/shared/lib/key-only-query-config";
import { getUserTenantId } from "@/domains/tenant/tenantId";
import { fetchRecordOwners } from "@/domains/record-owners";
import { EXTERNAL_RESOURCE_CONTENT_TYPE } from "@/domains/resources";
import type { RecordOwner } from "@/domains/record-owners";
import type { AnalysisRow, Json, TableRow } from "@/types/supabase.types";

type MediaRow = TableRow<"media">;
type MediaSettingRow = TableRow<"media_setting">;
type AnalysisMediaRow = TableRow<"analysis_media">;
type AnalysisMediaSettingRow = TableRow<"analysis_media_setting">;

type AnalysisRowWithSetting = Pick<AnalysisRow, "analysis_id"> & {
  analysis_setting: {
    display_name: string;
  };
};

export type ResourceListItem = {
  id: string;
  name: string;
  type: string;
  contentType: string;
  updatedAt: string;
  createdAt: string;
  owner: RecordOwner;
  container: string;
  objectKey: string;
};

export type ExternalResourceReport = {
  id: string;
  name: string;
};

export type ExternalResourceListItem = {
  id: string;
  title: string;
  sourceType: string;
  capturedAt: string;
  reports: ExternalResourceReport[];
  url: string | null;
};

export type ResourcesPageData = {
  uploadedResources: ResourceListItem[];
  externalResources: ExternalResourceListItem[];
};

const EXTERNAL_RESOURCE_LIMIT = 100;

function formatResourceType(row: MediaRow) {
  const extension = row.media_extension.trim();
  if (extension.length > 0) {
    return extension.toUpperCase();
  }

  const contentType = row.content_type.split("/").at(-1)?.trim() ?? "";
  return contentType.toUpperCase() || "FILE";
}

function asObject(
  value: Json | null | undefined,
): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }

  return value;
}

function getNestedValue(
  source: Record<string, unknown> | null,
  path: string[],
): unknown {
  let current: unknown = source;

  for (const segment of path) {
    if (!current || typeof current !== "object" || Array.isArray(current)) {
      return null;
    }

    current = (current as Record<string, unknown>)[segment];
  }

  return current;
}

function getStringFromSettings(
  settings: Json | null | undefined,
  paths: string[][],
): string | null {
  const source = asObject(settings);

  for (const path of paths) {
    const value = getNestedValue(source, path);
    if (typeof value === "string" && value.trim().length > 0) {
      return value.trim();
    }
  }

  return null;
}

function formatSourceType(value: string | null) {
  if (!value) return "External";

  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() + part.slice(1))
    .join(" ");
}

function getExternalResourceTitle(
  media: MediaRow,
  mediaSetting: MediaSettingRow | null | undefined,
  analysisMediaSetting: AnalysisMediaSettingRow | null | undefined,
) {
  return (
    getStringFromSettings(mediaSetting?.settings, [
      ["title"],
      ["name"],
      ["label"],
      ["metadata", "title"],
      ["source", "title"],
    ]) ??
    getStringFromSettings(analysisMediaSetting?.settings, [
      ["title"],
      ["name"],
      ["label"],
      ["metadata", "title"],
      ["source", "title"],
    ]) ??
    media.media_name
  );
}

function getExternalResourceUrl(
  mediaSetting: MediaSettingRow | null | undefined,
  analysisMediaSetting: AnalysisMediaSettingRow | null | undefined,
) {
  return (
    getStringFromSettings(mediaSetting?.settings, [
      ["url"],
      ["link"],
      ["href"],
      ["source_url"],
      ["sourceUrl"],
      ["metadata", "url"],
      ["source", "url"],
    ]) ??
    getStringFromSettings(analysisMediaSetting?.settings, [
      ["url"],
      ["link"],
      ["href"],
      ["source_url"],
      ["sourceUrl"],
      ["metadata", "url"],
      ["source", "url"],
    ]) ??
    null
  );
}

function getExternalResourceSourceType(
  mediaSetting: MediaSettingRow | null | undefined,
  analysisMediaSetting: AnalysisMediaSettingRow | null | undefined,
) {
  return formatSourceType(
    getStringFromSettings(mediaSetting?.settings, [
      ["source_type"],
      ["sourceType"],
      ["resource_type"],
      ["resourceType"],
      ["type"],
      ["metadata", "source_type"],
      ["metadata", "sourceType"],
      ["source", "type"],
    ]) ??
      getStringFromSettings(analysisMediaSetting?.settings, [
        ["source_type"],
        ["sourceType"],
        ["resource_type"],
        ["resourceType"],
        ["type"],
        ["metadata", "source_type"],
        ["metadata", "sourceType"],
        ["source", "type"],
      ]),
  );
}

export async function fetchResources(): Promise<ResourceListItem[]> {
  const userTenantId = await getUserTenantId();

  const { data, error } = await analysisClient
    .from("media")
    .select("*")
    .eq("record_tenant_id", userTenantId)
    .neq("content_type", EXTERNAL_RESOURCE_CONTENT_TYPE)
    .order("record_updated", { ascending: false });

  if (error) throw error;

  const rows = (data ?? []) as MediaRow[];
  const owners = await fetchRecordOwners(rows.map((row) => row.record_creator));

  return rows.map((row) => ({
    id: row.media_id,
    name: row.media_name,
    type: formatResourceType(row),
    contentType: row.content_type,
    updatedAt: row.record_updated,
    createdAt: row.record_created,
    owner: owners[row.record_creator] ?? {
      name: "Unknown user",
      initials: "??",
    },
    container: row.container,
    objectKey: row.object_key,
  }));
}

export async function fetchExternalResources(): Promise<
  ExternalResourceListItem[]
> {
  const userTenantId = await getUserTenantId();

  const { data: analysisMediaData, error: analysisMediaError } =
    await analysisClient
      .from("analysis_media")
      .select("analysis_media_id, analysis_id, media_id, record_updated")
      .eq("record_tenant_id", userTenantId)
      .order("record_updated", { ascending: false })
      .limit(EXTERNAL_RESOURCE_LIMIT);

  if (analysisMediaError) throw analysisMediaError;

  const analysisMediaRows = (analysisMediaData ?? []) as AnalysisMediaRow[];
  if (analysisMediaRows.length === 0) return [];

  const mediaIds = [...new Set(analysisMediaRows.map((row) => row.media_id))];
  const analysisIds = [
    ...new Set(analysisMediaRows.map((row) => row.analysis_id)),
  ];
  const analysisMediaIds = [
    ...new Set(analysisMediaRows.map((row) => row.analysis_media_id)),
  ];

  const [
    mediaResult,
    mediaSettingResult,
    analysisResult,
    analysisMediaSettingResult,
  ] = await Promise.all([
    analysisClient
      .from("media")
      .select(
        "container, content_type, media_checksum, media_extension, media_id, media_name, object_key, record_created, record_creator, record_tenant_id, record_updated, record_updator",
      )
      .in("media_id", mediaIds),
    analysisClient
      .from("media_setting")
      .select("media_id, settings")
      .in("media_id", mediaIds),
    analysisClient
      .from("analysis")
      .select("analysis_id, analysis_setting!inner(display_name)")
      .in("analysis_id", analysisIds),
    analysisClient
      .from("analysis_media_setting")
      .select("analysis_media_id, settings")
      .in("analysis_media_id", analysisMediaIds),
  ]);

  if (mediaResult.error) throw mediaResult.error;
  if (mediaSettingResult.error) throw mediaSettingResult.error;
  if (analysisResult.error) throw analysisResult.error;
  if (analysisMediaSettingResult.error) throw analysisMediaSettingResult.error;

  const mediaRows = (mediaResult.data ?? []) as MediaRow[];
  const externalMediaRows = mediaRows.filter(
    (row) => row.content_type === EXTERNAL_RESOURCE_CONTENT_TYPE,
  );

  if (externalMediaRows.length === 0) return [];

  const externalMediaIds = new Set(
    externalMediaRows.map((row) => row.media_id),
  );
  const relevantAnalysisMediaRows = analysisMediaRows.filter((row) =>
    externalMediaIds.has(row.media_id),
  );

  const mediaById = new Map(
    externalMediaRows.map((row) => [row.media_id, row]),
  );
  const mediaSettingById = new Map(
    ((mediaSettingResult.data ?? []) as MediaSettingRow[]).map((row) => [
      row.media_id,
      row,
    ]),
  );
  const analysisById = new Map(
    ((analysisResult.data ?? []) as AnalysisRowWithSetting[]).map((row) => [
      row.analysis_id,
      row,
    ]),
  );
  const analysisMediaSettingById = new Map(
    ((analysisMediaSettingResult.data ?? []) as AnalysisMediaSettingRow[]).map(
      (row) => [row.analysis_media_id, row],
    ),
  );

  const externalResources = new Map<string, ExternalResourceListItem>();

  for (const row of relevantAnalysisMediaRows) {
    const media = mediaById.get(row.media_id);
    if (!media) continue;

    const mediaSetting = mediaSettingById.get(row.media_id);
    const analysisMediaSetting = analysisMediaSettingById.get(
      row.analysis_media_id,
    );
    const analysis = analysisById.get(row.analysis_id);
    const reportName =
      analysis?.analysis_setting?.display_name?.trim() || "Untitled report";

    const existing = externalResources.get(row.media_id);
    const capturedAt =
      existing && existing.capturedAt > row.record_updated
        ? existing.capturedAt
        : row.record_updated;

    const nextReports = existing ? [...existing.reports] : [];
    if (!nextReports.some((report) => report.id === row.analysis_id)) {
      nextReports.push({
        id: row.analysis_id,
        name: reportName,
      });
    }

    externalResources.set(row.media_id, {
      id: row.media_id,
      title: getExternalResourceTitle(
        media,
        mediaSetting,
        analysisMediaSetting,
      ),
      sourceType: getExternalResourceSourceType(
        mediaSetting,
        analysisMediaSetting,
      ),
      capturedAt,
      reports: nextReports,
      url: getExternalResourceUrl(mediaSetting, analysisMediaSetting),
    });
  }

  return [...externalResources.values()].sort(
    (left, right) =>
      new Date(right.capturedAt).getTime() -
      new Date(left.capturedAt).getTime(),
  );
}

export async function fetchResourcesPageData(): Promise<ResourcesPageData> {
  const [uploadedResources, externalResources] = await Promise.all([
    fetchResources(),
    fetchExternalResources(),
  ]);

  return {
    uploadedResources,
    externalResources,
  };
}

const resourcesBase = getKeyOnlyQueryConfig(["resources"]);

export const resourceQueries = {
  all: resourcesBase,
  list: () =>
    queryOptions({
      queryKey: [...resourcesBase.queryKey, "list"],
      queryFn: fetchResources,
    }),
  externalList: () =>
    queryOptions({
      queryKey: [...resourcesBase.queryKey, "external-list"],
      queryFn: fetchExternalResources,
    }),
  pageData: () =>
    queryOptions({
      queryKey: [...resourcesBase.queryKey, "page-data"],
      queryFn: fetchResourcesPageData,
    }),
};
